import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { Plugin, PluginKey } from 'prosemirror-state'
import {
  FillableElementPosition,
  PosKey,
  RelativeCoords,
} from '../../types/fillable-element'

import { Extension } from '@tiptap/core'

export interface PositionMapping {
  [key: string]: FillableElementPosition
}

// Create a plugin key for position markers
export const positionMarkerPluginKey = new PluginKey('position-markers')

function createPositionMarkerPlugin(
  debugMode: boolean,
  getElementRefById: (id: string) => HTMLElement,
  isPrintAdjustToConsider: boolean
) {
  let currentPositions: PositionMapping = {}
  return new Plugin({
    key: positionMarkerPluginKey,
    state: {
      init() {
        return DecorationSet.empty
      },
      apply(tr) {
        const positions = tr.getMeta(positionMarkerPluginKey) as PositionMapping
        if (positions) {
          currentPositions = positions
        }

        const decorations: Decoration[] = []

        Object.entries(currentPositions).forEach(
          ([id, position]: [string, FillableElementPosition]) => {
            const posToConsiderKey = position.posToConsiderKey

            const posToConsider = position.markerPos?.[posToConsiderKey]?.pos

            if (posToConsider) {
              if (isPrintAdjustToConsider) {
                return
              }
              decorations.push(
                Decoration.widget(
                  posToConsider,
                  () => {
                    // Create marker container
                    const marker = document.createElement('span')
                    marker.className = 'position-marker'
                    marker.setAttribute('data-marker-id', id)
                    marker.setAttribute(
                      'data-pos-to-consider-key',
                      posToConsiderKey
                    )
                    marker.setAttribute(
                      'data-quadrant',
                      position.quadrant?.toString() ?? 'null'
                    )
                    marker.id = `marker-${id}`
                    marker.style.cssText = `
                      position: absolute;
                      display: inline-block !important;
                      width: ${debugMode ? '8px' : '0px'} !important;
                      height: ${debugMode ? '8px' : '0px'} !important;
                      max-width: inherit !important;
                      max-height: inherit !important;
                      background-color: ${getMarkerColor(
                        posToConsiderKey,
                        debugMode
                      )} !important;
                      border-radius: 50% !important;
                      pointer-events: none !important;

                      z-index: 9998 !important;
                    `
                    return marker
                  },
                  {
                    key: `marker-${id}`,
                    side: 1,
                    ignoreSelection: true,
                  }
                )
              )
            }
          }
        )

        return DecorationSet.create(tr.doc, decorations)
      },
    },
    props: {
      decorations(state) {
        return this.getState(state)
      },
    },
  })
}

export const PositionMarkerExtension = Extension.create<{
  debugMode: boolean
  getElementRefById: (id: string) => HTMLElement
  isPrintAdjustToConsider: boolean
}>({
  name: 'positionMarker',

  addOptions() {
    return {
      debugMode: false,
      getElementRefById: () => {
        throw new Error('getElementRefById function must be provided')
      },
      isPrintAdjustToConsider: false,
    }
  },

  addProseMirrorPlugins() {
    return [
      createPositionMarkerPlugin(
        this.options.debugMode,
        this.options.getElementRefById,
        this.options.isPrintAdjustToConsider
      ),
    ]
  },
})

function getMarkerColor(posToConsiderKey: PosKey, debugMode: boolean) {
  if (!debugMode) {
    return 'transparent'
  }
  switch (posToConsiderKey) {
    case PosKey.posFromLeftTop:
      return 'blue'
    case PosKey.posFromLeftBottom:
      return 'purple'
    case PosKey.posFromCenterTop:
      return 'green'
    case PosKey.posFromCenterBottom:
      return 'orange'
    case PosKey.posFromRightTop:
      return 'red'
    case PosKey.posFromRightBottom:
      return 'yellow'
    case PosKey.posFromNodeDom:
      return 'aqua'
    default:
      return 'transparent'
  }
}

function createElementInsideMarker(
  marker: HTMLElement,
  originalElement: HTMLElement,
  posRelativeCoords: RelativeCoords,
  posToConsiderKey: PosKey,
  id: string,
  debugMode: boolean
) {
  const clonedElement = originalElement.cloneNode(true) as HTMLElement
  // Get original element dimensions
  const originalRect = originalElement.getBoundingClientRect()

  // Create a wrapper for the cloned element
  const wrapper = document.createElement('span')
  wrapper.style.cssText = `
    position: absolute !important;
    display: block !important;
    top: ${posRelativeCoords?.top}px !important;
    left: ${posRelativeCoords?.left}px !important;
    width: ${originalRect.width}px !important;
    height: ${originalRect.height}px !important;
    pointer-events: none !important;
    max-width: none !important;
    z-index: 9999 !important;
    background: ${debugMode ? 'gray' : 'transparent'} !important;
    overflow: visible !important;
    text-align: left !important;
    justify-content: unset !important;
    align-items: unset !important;
    text-align-last: left !important;
    font-family: Inter, Roboto, Arial, sans-serif !important;
    ${
      debugMode
        ? 'border: 2px solid ' +
          getMarkerColor(posToConsiderKey, debugMode) +
          ' !important;'
        : ''
    }
  `
  // Style the cloned element
  clonedElement.style.cssText = `
    position: static !important;
    width: ${originalRect.width}px !important;
    height: ${originalRect.height}px !important;
    pointer-events: none !important;
    margin: 0 !important;
    padding: 0 !important;
    visibility: visible !important;
    opacity: 1 !important;
  `
  clonedElement.id = `cloned-${id}`
  clonedElement.style.opacity = '1'
  //Hiding the original element
  originalElement.style.opacity = '0'

  // In the case of checkbox width is added using v-bind and wont get applied on clone so set it manually
  const checkboxNode = clonedElement.querySelector('.n-checkbox') as HTMLElement

  if (checkboxNode) {
    // Set dimensions for both the field and checkbox
    const width = `${originalRect.width}px`
    const height = `${originalRect.height}px`

    checkboxNode.style.cssText = `
      --n-border: 1px solid #FFFFFF !important;
      padding: 0 !important;
      width: ${width} !important;
      height: ${height} !important;
      --n-size: ${width} !important;
      display: block !important;
      --n-label-line-height: 1.6;
    --n-label-font-weight: 400;
    --n-size: 18px;
    --n-bezier: cubic-bezier(.4, 0, .2, 1);
    --n-border-radius: 2px;
    --n-border: 1px solid rgb(224, 224, 230);
    --n-border-checked: 1px solid #155EEF;
    --n-border-focus: 1px solid #155EEF;
    --n-border-disabled: 1px solid rgb(224, 224, 230);
    --n-border-disabled-checked: 1px solid rgb(224, 224, 230);
    --n-box-shadow-focus: 0 0 0 2px rgba(21, 94, 239, 0.3);
    --n-color: #FFF;
    --n-color-checked: #155EEF;
    --n-color-table: #fff;
    --n-color-table-modal: #fff;
    --n-color-table-popover: #fff;
    --n-color-disabled: rgb(250, 250, 252);
    --n-color-disabled-checked: rgb(250, 250, 252);
    --n-text-color: rgba(52, 64, 84, 1);
    --n-text-color-disabled: rgba(194, 194, 194, 1);
    --n-check-mark-color: #FFF;
    --n-check-mark-color-disabled: rgba(194, 194, 194, 1);
    --n-check-mark-color-disabled-checked: rgba(194, 194, 194, 1);
    --n-font-size: 15px;
    --n-label-padding: 0 8px;

    `
  }

  wrapper.appendChild(clonedElement)
  marker.appendChild(wrapper)
}

export function createElementsInsideEmptyMarkers(
  fillableElements: {
    id: string
    value: FillableElementPosition
  }[],
  debugMode: boolean,
  isPrintAdjustToConsider: boolean
) {
  if (isPrintAdjustToConsider) return
  fillableElements.forEach(element => {
    const markerElem = document.getElementById(`marker-${element.id}`)
    if (markerElem) {
      const markerRect = markerElem.getBoundingClientRect()
      const originalElement = document.getElementById(element.id)
      const originalRect = originalElement?.getBoundingClientRect()
      const posToConsiderKey = element.value.posToConsiderKey

      const posRelativeCoords = {
        top: (originalRect?.top ?? 0) - markerRect.top,
        bottom: (originalRect?.bottom ?? 0) - markerRect.top,
        left: (originalRect?.left ?? 0) - markerRect.left,
        right: (originalRect?.right ?? 0) - markerRect.left,
      }
      createElementInsideMarker(
        markerElem,
        originalElement as HTMLElement,
        posRelativeCoords,
        posToConsiderKey,
        element.id,
        debugMode
      )
    }
  })
}
